import { useCallback, useEffect, useRef, useState } from 'react';
import { useSpeechInput } from './useSpeechInput';
import { useSpeechSynthesis } from './useSpeechSynthesis';

/**
 * Push-to-talk + optional hands-free conversation loop for demos.
 * When conversationMode is on, final transcripts call onUtterance, and
 * speakReply() reads responses then restarts listening.
 *
 * @param {{ onUtterance?: (text: string) => void, onDictate?: (text: string) => void }} opts
 */
export function useVoiceConversation({ onUtterance, onDictate } = {}) {
  const [conversationMode, setConversationMode] = useState(false);
  const conversationModeRef = useRef(false);
  const lastSpokenRef = useRef('');
  const onUtteranceRef = useRef(onUtterance);
  const onDictateRef = useRef(onDictate);
  onUtteranceRef.current = onUtterance;
  onDictateRef.current = onDictate;
  conversationModeRef.current = conversationMode;

  const { speak, cancel: cancelSpeech, isSpeaking, hasSupport: hasTts } = useSpeechSynthesis();

  const handleTranscript = useCallback((text) => {
    const cleaned = String(text || '').trim();
    if (!cleaned) return;
    if (conversationModeRef.current && onUtteranceRef.current) {
      onUtteranceRef.current(cleaned);
      return;
    }
    if (onDictateRef.current) onDictateRef.current(cleaned);
  }, []);

  const {
    isListening, startListening, stopListening, hasSupport: hasStt,
  } = useSpeechInput(handleTranscript);

  const stopAll = useCallback(() => {
    stopListening();
    cancelSpeech();
  }, [stopListening, cancelSpeech]);

  const setMode = useCallback((next) => {
    const enabled = Boolean(next);
    setConversationMode(enabled);
    conversationModeRef.current = enabled;
    if (!enabled) {
      stopAll();
      lastSpokenRef.current = '';
    }
  }, [stopAll]);

  const toggleConversationMode = useCallback(() => {
    setMode(!conversationModeRef.current);
  }, [setMode]);

  const speakReply = useCallback((text, { force = false } = {}) => {
    const cleaned = String(text || '').replace(/\s+/g, ' ').trim();
    if (!cleaned) return;
    if (!force && cleaned === lastSpokenRef.current) return;
    lastSpokenRef.current = cleaned;
    stopListening();
    speak(cleaned, {
      onEnd: () => {
        if (conversationModeRef.current) {
          try {
            startListening();
          } catch {
            /* ignore */
          }
        }
      },
    });
  }, [speak, stopListening, startListening]);

  useEffect(() => () => stopAll(), [stopAll]);

  return {
    conversationMode,
    setConversationMode: setMode,
    toggleConversationMode,
    isListening,
    isSpeaking,
    startListening,
    stopListening,
    stopAll,
    speakReply,
    cancelSpeech,
    hasSupport: hasStt || hasTts,
    hasStt,
    hasTts,
  };
}
